import { createSignal, createEffect, onCleanup, untrack, For, Show } from 'solid-js';
import { useNavigate } from '@solidjs/router';
import Text from '../components/Text';
import SellbotNav from '../components/SellbotNav';
import HologramPreview from '../components/HologramPreview';
import HologramExclusionsCard from '../components/HologramExclusionsCard';
import { EmptyState } from '../components/EmptyState';
import { SkeletonCard } from '../components/Skeleton';
import { showConfirm } from '../components/Dialog';
import { useToast } from '../components/MessageBox';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import { useActiveBot } from '../state/activeBot';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { subscribeBotEngineEvents } from '../services/botEngineEvents';
import type { PlayerListDto, SellbotHologramDto, SellbotHologramExclusionsDto, SellbotHologramLiveDto } from '../api';
import sellbotStyles from './Sellbot.module.css';
import styles from './SellbotHolograms.module.css';

export default function SellbotHolograms() {
    const api = useApi();
    const navigate = useNavigate();
    const toast = useToast();
    const { t } = useTranslation();
    const { botId } = useActiveBot();

    const [loading, setLoading] = createSignal(true);
    const [holograms, setHolograms] = createSignal<SellbotHologramDto[]>([]);
    const [live, setLive] = createSignal<SellbotHologramLiveDto | null>(null);
    const [refreshing, setRefreshing] = createSignal(false);
    const [exclusions, setExclusions] = createSignal<SellbotHologramExclusionsDto>({ players: [], lists: [] });

    const loadAll = async () => {
        const id = botId();
        if (!id) { setHolograms([]); setLive(null); setLoading(false); return; }

        const [layouts, inGame, excluded] = await Promise.all([
            api.sellbotHologram.list(id),
            api.sellbotHologram.live(id),
            api.sellbotHologram.exclusions(id),
        ]);

        setHolograms(isOk(layouts.data) ? layouts.data.value : []);
        setLive(isOk(inGame.data) ? inGame.data.value : null);
        if (isOk(excluded.data)) setExclusions(excluded.data.value);
        setLoading(false);
    };

    createEffect(() => {
        const id = botId();
        setLoading(true);
        untrack(() => { void loadAll(); });
        if (!id) return;
        const unsub = subscribeBotEngineEvents(id, (evt) => {
            if (evt.type === 'plugin_event' && evt.payload?.source === 'sellbot') void loadAll();
        });
        onCleanup(unsub);
    });

    const requestRefresh = async () => {
        const id = botId();
        if (!id) return;
        setRefreshing(true);
        const res = await api.sellbotHologram.refreshLive(id);
        setRefreshing(false);
        if (isOk(res.data)) toast?.info(t('HOLO_TOAST_REFRESH_QUEUED'));
        else toast?.error(firstError(res.data) ?? t('HOLO_TOAST_REFRESH_FAILED'));
    };

    const remove = async (hologram: SellbotHologramDto) => {
        const id = botId();
        if (!id) return;
        if (!await showConfirm(t('HOLO_CONFIRM_DELETE', hologram.title || String(hologram.pholoName)), true)) return;
        const res = await api.sellbotHologram.delete(id, hologram.id);
        if (isOk(res.data)) { toast?.success(t('HOLO_TOAST_DELETED')); void loadAll(); }
        else toast?.error(firstError(res.data) ?? t('HOLO_TOAST_DELETE_FAILED'));
    };

    // Every exclusion endpoint answers with the shop-wide set as it now stands.
    const applyExclusionChange = (res: Awaited<ReturnType<typeof api.sellbotHologram.exclusions>>) => {
        if (isOk(res.data)) setExclusions(res.data.value);
        else toast?.error(firstError(res.data) ?? t('HOLO_TOAST_EXCLUDE_FAILED'));
    };

    const excludePlayer = async (playerName: string) => {
        const id = botId();
        if (id) applyExclusionChange(await api.sellbotHologram.addExcludedPlayer(id, { playerName, playerUuid: null }));
    };

    const includePlayer = async (player: { id: string }) => {
        const id = botId();
        if (id) applyExclusionChange(await api.sellbotHologram.removeExcludedPlayer(id, player.id));
    };

    const excludeList = async (list: PlayerListDto) => {
        const id = botId();
        if (id) applyExclusionChange(await api.sellbotHologram.attachExcludedList(id, list.id));
    };

    const includeList = async (playerListId: string) => {
        const id = botId();
        if (id) applyExclusionChange(await api.sellbotHologram.detachExcludedList(id, playerListId));
    };

    const stateClass = (state: string) => {
        if (state === 'Active') return styles.stateActive;
        if (state === 'Conflict') return styles.stateConflict;
        return styles.stateMissing;
    };

    const unclaimed = () => (live()?.entries ?? []).filter(entry => !entry.isClaimed);

    const hasListed = () => live()?.lastListedAt != null;

    return (
        <div class={sellbotStyles.container}>
            <SellbotNav />

            <div class={sellbotStyles.headerRow}>
                <div class="v" style={{ gap: '4px' }}>
                    <h1><Text val="HOLO_TITLE" /></h1>
                    <span class={sellbotStyles.heroSubtitle}><Text val="HOLO_SUBTITLE" /></span>
                </div>
                <button class="btn btn-secondary" onClick={() => void requestRefresh()} disabled={refreshing() || !botId()}>
                    <i class="fa-solid fa-rotate" />
                    <Text val="HOLO_REFRESH" />
                </button>
            </div>

            <Show when={!loading()} fallback={<div class="v" style={{ gap: 'var(--spacing-md)' }}><SkeletonCard /><SkeletonCard /></div>}>
                <Show
                    when={holograms().length > 0 || unclaimed().length > 0}
                    fallback={
                        <EmptyState
                            illustration="generic"
                            title={t(hasListed() ? 'HOLO_EMPTY_TITLE' : 'HOLO_NEVER_LISTED_TITLE')}
                            hint={hasListed()
                                ? t('HOLO_EMPTY_HINT', live()?.citybuild || t('HOLO_THIS_CITYBUILD'))
                                : t('HOLO_NEVER_LISTED_HINT')}
                        />
                    }
                >
                    <Show when={holograms().length > 0}>
                        <h2 class={styles.sectionTitle}><Text val="HOLO_SECTION_LAYOUTS" /></h2>
                        <div class={styles.grid}>
                            <For each={holograms()}>
                                {(hologram) => (
                                    <div class={`fcard v ${styles.card}`}>
                                        <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                                            <div class="v" style={{ gap: '2px', 'min-width': '0' }}>
                                                <strong class={styles.cardTitle}>{hologram.title || t('HOLO_UNTITLED')}</strong>
                                                <span class={styles.cardMeta}>
                                                    <Text val="HOLO_CARD_NAME" /> {hologram.pholoName}
                                                    <Show when={hologram.citybuild}> · {hologram.citybuild}</Show>
                                                    <Show when={hologram.lastListedAt}> · {hologram.x} {hologram.y} {hologram.z}</Show>
                                                </span>
                                            </div>
                                            <span class={`${styles.state} ${stateClass(hologram.state)}`}>
                                                <Text val={'HOLO_STATE_' + hologram.state.toUpperCase()} />
                                            </span>
                                        </div>

                                        <HologramPreview lines={hologram.pushedLines} scale={1.5} />

                                        <Show when={hologram.lastError}>
                                            <p class={styles.error}>{hologram.lastError}</p>
                                        </Show>

                                        <div class="h hcenter" style={{ 'justify-content': 'space-between', gap: 'var(--spacing-sm)' }}>
                                            <span class={styles.cardMeta}>
                                                <Show when={hologram.lastPushedAt} fallback={<Text val="HOLO_NEVER_PUSHED" />}>
                                                    <Text val="HOLO_LAST_PUSHED" /> {formatDateTime(hologram.lastPushedAt!)}
                                                </Show>
                                            </span>
                                            <div class="h" style={{ gap: 'var(--spacing-xs)' }}>
                                                <button class="btn btn-secondary" onClick={() => navigate(`/sellbot/holograms/${hologram.id}`)}>
                                                    <i class="fa-solid fa-pen" />
                                                    <Text val="HOLO_EDIT" />
                                                </button>
                                                <button class="btn btn-icon btn-danger" title={t("HOLO_DELETE")} onClick={() => void remove(hologram)}>
                                                    <i class="fa-solid fa-trash" />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </For>
                        </div>

                        <HologramExclusionsCard
                            titleKey="HOLO_EXCLUDE_SHOP_TITLE" helpKey="HOLO_EXCLUDE_SHOP_HELP"
                            players={exclusions().players} lists={exclusions().lists}
                            onAddPlayer={excludePlayer} onRemovePlayer={includePlayer}
                            onAttachList={excludeList} onDetachList={includeList} />
                    </Show>

                    <Show when={unclaimed().length > 0}>
                        <h2 class={styles.sectionTitle}><Text val="HOLO_SECTION_UNCLAIMED" /></h2>
                        <p class={styles.sectionHint}><Text val="HOLO_SECTION_UNCLAIMED_HINT" /></p>
                        <div class={styles.grid}>
                            <For each={unclaimed()}>
                                {(entry) => (
                                    <div class={`fcard v ${styles.card}`}>
                                        <div class="v" style={{ gap: '2px' }}>
                                            <strong class={styles.cardTitle}>
                                                <Text val="HOLO_CARD_NAME" /> {entry.pholoName}
                                            </strong>
                                            <span class={styles.cardMeta}>{entry.x} {entry.y} {entry.z}</span>
                                        </div>
                                        <HologramPreview lines={entry.lines} scale={1.5} />
                                        <button class="btn btn-primary" onClick={() => navigate(`/sellbot/holograms/new?name=${entry.pholoName}`)}>
                                            <i class="fa-solid fa-plus" />
                                            <Text val="HOLO_CLAIM" />
                                        </button>
                                    </div>
                                )}
                            </For>
                        </div>
                    </Show>
                </Show>
            </Show>
        </div>
    );
}
